import React from 'react';

import { NeverChildren } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';

import animation from './animation';
import FillableContainer from './FillableContainer';
import SpinnerRing from './SpinnerRing';

export interface Props extends NeverChildren {
  /**
   * Whether the spinner should fill its container. Defaults to `true`.
   */
  fill?: boolean;
  /**
   * The minimum opacity for the spinner.
   */
  minOpacity?: number;
  /**
   * The height and width of the spinner.
   */
  size?: number;
  /**
   * The stroke color for the spinner.
   */
  strokeColor?: string;
  /**
   * The stroke width for the spinner.
   */
  strokeWidth?: number;
}

const SpinnerDiv = styled('div', { shouldForwardProp: (p) => p !== 'size' })<{
  size: number;
}>({ animation }, ({ size }) => ({ height: size, width: size }));

/**
 * A simple spinner.
 */
const Spinner = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      fill = true,
      minOpacity = 0.2,
      size = 32,
      strokeColor = 'currentColor',
      strokeWidth = Math.log2(size / 2),
    },
    ref,
  ) => (
    <FillableContainer innerRef={ref} fill={fill}>
      <SpinnerDiv size={size}>
        <SpinnerRing
          size={size}
          minOpacity={minOpacity}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SpinnerDiv>
    </FillableContainer>
  ),
);

export default Spinner;
